import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronRight,
  Pencil,
  ArrowUpDown,
  Hourglass,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePromotions, useDeletePromotion, useDuplicatePromotion, useEndPromotionEarly } from '@/hooks/usePromotions';
import type { PromotionTab } from '@/hooks/usePromotions';
import { PromotionDetailsExpanded } from '@/components/promotions/PromotionDetailsExpanded';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

export default function PromotionsList() {
  const navigate = useNavigate();
  const { organization } = useAuth();

  // Filters
  const [tab, setTab] = useState<PromotionTab>('current');
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Dialogs
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [endEarlyId, setEndEarlyId] = useState<string | null>(null);
  const [endEarlyMode, setEndEarlyMode] = useState<'now' | 'date'>('now');
  const [endEarlyDate, setEndEarlyDate] = useState('');
  const [viewProductsId, setViewProductsId] = useState<string | null>(null);

  // Data
  const { data: promotions = [], isLoading } = usePromotions({
    tab,
    search: search || undefined,
    locationId: locationFilter || undefined,
  });

  const deletePromotion = useDeletePromotion();
  const duplicatePromotion = useDuplicatePromotion();
  const endPromotionEarly = useEndPromotionEarly();

  // Fetch locations for filter dropdown
  const { data: locations = [] } = useQuery({
    queryKey: ['locations', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data } = await supabase
        .from('locations')
        .select('id, name')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('name');
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch products for view products dialog
  const { data: viewProducts = [] } = useQuery({
    queryKey: ['promotion-view-products', viewProductsId],
    queryFn: async () => {
      if (!viewProductsId) return [];
      // Simple fetch of products matching the promotion rules
      const { data: rules } = await supabase
        .from('promotion_product_rules')
        .select('*')
        .eq('promotion_id', viewProductsId);

      const includeRules = (rules || []).filter((r: any) => r.rule_group.endsWith('_include'));

      if (includeRules.length === 0) {
        // All products (return first 100)
        const { data } = await supabase
          .from('products')
          .select('id, name, sku, price')
          .eq('is_active', true)
          .order('name')
          .limit(100);
        return data || [];
      }

      const productIds = includeRules.filter((r: any) => r.filter_type === 'product').map((r: any) => r.filter_value);
      const categoryIds = includeRules.filter((r: any) => r.filter_type === 'category').map((r: any) => r.filter_value);

      const orParts: string[] = [];
      if (productIds.length > 0) orParts.push(`id.in.(${productIds.join(',')})`);
      if (categoryIds.length > 0) orParts.push(`category_id.in.(${categoryIds.join(',')})`);

      if (orParts.length === 0) return [];

      const { data } = await supabase
        .from('products')
        .select('id, name, sku, price')
        .eq('is_active', true)
        .or(orParts.join(','))
        .order('name')
        .limit(100);

      return data || [];
    },
    enabled: !!viewProductsId,
  });

  // Sort promotions by start date descending
  const sortedPromotions = useMemo(() => {
    return [...promotions].sort((a, b) => {
      const dateA = a.start_datetime || a.created_at;
      const dateB = b.start_datetime || b.created_at;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  }, [promotions]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'No end date';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deletePromotion.mutateAsync(deleteId);
      toast.success('Promotion deleted');
      setDeleteId(null);
      setExpandedId(null);
    } catch (err: any) {
      toast.error('Failed to delete promotion: ' + err.message);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await duplicatePromotion.mutateAsync(id);
      toast.success('Promotion duplicated');
    } catch (err: any) {
      toast.error('Failed to duplicate: ' + err.message);
    }
  };

  const handleEndEarly = async () => {
    if (!endEarlyId) return;
    try {
      await endPromotionEarly.mutateAsync({
        id: endEarlyId,
        endNow: endEarlyMode === 'now',
        newEndDate: endEarlyMode === 'date' ? new Date(endEarlyDate).toISOString() : undefined,
      });
      toast.success('Promotion ended');
      setEndEarlyId(null);
      setExpandedId(null);
    } catch (err: any) {
      toast.error('Failed to end promotion: ' + err.message);
    }
  };

  const tabs: { key: PromotionTab; label: string }[] = [
    { key: 'current', label: 'Current and upcoming' },
    { key: 'past', label: 'Past' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center gap-6 border-b">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Description + Add Button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {tab === 'current'
            ? 'Create and manage current and upcoming promotions.'
            : tab === 'past'
              ? 'Past promotions will be displayed here. You can also duplicate and repeat promotions that have been successful.'
              : 'All promotions across all time periods.'}
        </p>
        <Button onClick={() => navigate('/products/promotions/new')} className="gap-2">
          <Plus className="h-4 w-4" />
          Add promotion
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-end">
        <div className="flex-1 max-w-xs">
          <label className="text-sm font-medium mb-1.5 block">Search for promotions</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Enter promotion name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="w-48">
          <label className="text-sm font-medium mb-1.5 block">Outlet</label>
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Select an outlet" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All outlets</SelectItem>
              {locations.map((loc: any) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="default"
          onClick={() => {
            // Trigger refetch by changing filter state
          }}
        >
          Search
        </Button>
      </div>

      {/* Count */}
      <p className="text-sm text-muted-foreground">
        Displaying {sortedPromotions.length} promotion{sortedPromotions.length !== 1 ? 's' : ''}
      </p>

      {/* Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">
          Loading promotions...
        </div>
      ) : sortedPromotions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Hourglass className="h-16 w-16 text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">
            {tab === 'past'
              ? 'Past promotions will be displayed here. You can also duplicate and repeat promotions that have been successful.'
              : tab === 'current'
                ? 'No current or upcoming promotions. Click "Add promotion" to create one.'
                : 'No promotions found.'}
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[40px_1fr_1fr_120px_1fr_120px_40px] gap-4 px-4 py-3 bg-muted/50 text-sm font-medium text-muted-foreground border-b">
            <div />
            <div className="flex items-center gap-1 cursor-pointer hover:text-foreground">
              Start date
              <ArrowUpDown className="h-3 w-3" />
            </div>
            <div className="flex items-center gap-1 cursor-pointer hover:text-foreground">
              End date
              <ArrowUpDown className="h-3 w-3" />
            </div>
            <div>Recurring</div>
            <div className="flex items-center gap-1 cursor-pointer hover:text-foreground">
              Name
              <ArrowUpDown className="h-3 w-3" />
            </div>
            <div>Available on</div>
            <div />
          </div>

          {/* Rows */}
          {sortedPromotions.map((promo) => {
            const isExpanded = expandedId === promo.id;
            const locationCount = promo.promotion_locations?.length || 0;
            const isPast = promo.end_datetime && new Date(promo.end_datetime) < new Date();

            return (
              <div key={promo.id} className="border-b last:border-b-0">
                {/* Main row */}
                <div
                  className="grid grid-cols-[40px_1fr_1fr_120px_1fr_120px_40px] gap-4 px-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors items-center"
                  onClick={() => setExpandedId(isExpanded ? null : promo.id)}
                >
                  <div className="flex justify-center">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="text-sm">
                    <div>{formatDate(promo.start_datetime)}</div>
                    {!isPast && promo.is_active && (
                      <Badge variant="secondary" className="mt-1 text-[10px] px-1.5 py-0">
                        Current
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm">
                    {promo.end_datetime ? formatDate(promo.end_datetime) : 'No end date'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {promo.schedule_type === 'recurring' ? 'Yes' : '-'}
                  </div>
                  <div className="text-sm font-medium">{promo.name}</div>
                  <div className="text-sm">
                    {locationCount} outlet{locationCount !== 1 ? 's' : ''}
                  </div>
                  <div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/products/promotions/edit/${promo.id}`);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <PromotionDetailsExpanded
                    promotion={promo}
                    onEdit={() => navigate(`/products/promotions/edit/${promo.id}`)}
                    onViewProducts={() => setViewProductsId(promo.id)}
                    onDuplicate={() => handleDuplicate(promo.id)}
                    onEndEarly={() => setEndEarlyId(promo.id)}
                    onDelete={() => setDeleteId(promo.id)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete promotion?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this promotion from your records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End Early Dialog */}
      <Dialog open={!!endEarlyId} onOpenChange={(open) => !open && setEndEarlyId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End promotion early</DialogTitle>
            <DialogDescription>
              Choose when to end this promotion.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-3">
              <Button
                variant={endEarlyMode === 'now' ? 'default' : 'outline'}
                onClick={() => setEndEarlyMode('now')}
                className="flex-1"
              >
                End now
              </Button>
              <Button
                variant={endEarlyMode === 'date' ? 'default' : 'outline'}
                onClick={() => setEndEarlyMode('date')}
                className="flex-1"
              >
                Set new end date
              </Button>
            </div>
            {endEarlyMode === 'date' && (
              <Input
                type="datetime-local"
                value={endEarlyDate}
                onChange={(e) => setEndEarlyDate(e.target.value)}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndEarlyId(null)}>
              Cancel
            </Button>
            <Button onClick={handleEndEarly}>
              {endEarlyMode === 'now' ? 'End Promotion' : 'Set end date'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Products Dialog */}
      <Dialog open={!!viewProductsId} onOpenChange={(open) => !open && setViewProductsId(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Products in promotion</DialogTitle>
            <DialogDescription>
              {viewProducts.length} product{viewProducts.length !== 1 ? 's' : ''} included
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            {viewProducts.map((product: any) => (
              <div key={product.id} className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/50">
                <div>
                  <p className="text-sm font-medium">{product.name}</p>
                  {product.sku && (
                    <p className="text-xs text-muted-foreground">SKU: {product.sku}</p>
                  )}
                </div>
                <span className="text-sm font-medium">${product.price?.toFixed(2)}</span>
              </div>
            ))}
            {viewProducts.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No products found for this promotion.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
